import { router } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { Subject, asyncScheduler, exhaustMap, filter, throttleTime } from "rxjs"

import { getCsrfToken } from "@/lib/csrf"

import type { TCheckinForm } from "./stores/useCheckinFormStore"
import {
  useCheckinForm,
  useCheckinSubscriber,
} from "./stores/useCheckinFormStore"

/**
 * Options for the autosave hook.
 * lock_version is kept outside of the form store to prevent autosave
 * cycles — updating it after a save would mark the form as dirty and
 * trigger another save.
 */
interface UseCheckinFormSyncOptions {
  token: string
  lockVersion: number
}

/**
 * Manages autosave and final submission for a checkin form.
 *
 * Saves are driven by two streams merged together:
 * - throttledChanges: debounces rapid user input before hitting the server
 * - heartbeat: ensures the latest state is persisted even if no changes occur
 *
 * Uses optimistic locking via lock_version to detect concurrent edits.
 * A 409 conflict means another session saved ahead of this one.
 */
const useAutoSave = ({ token, lockVersion }: UseCheckinFormSyncOptions) => {
  const formData = useCheckinForm((s) => s.data)
  const lockVersionRef = useRef(lockVersion)
  const [conflict, setConflict] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [autoSave$] = useState(() => new Subject<Partial<TCheckinForm>>())

  useEffect(() => {
    const sub = autoSave$
      .pipe(
        throttleTime(500, asyncScheduler, { leading: false, trailing: true }),
        filter((state) => Object.keys(state).length > 0),
        exhaustMap(async (state) => {
          setSaving(true)
          const response = await fetch(`/forms/checkins/${token}`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": getCsrfToken(),
              Accept: "application/json",
            },
            body: JSON.stringify({
              answers: state,
              lock_version: lockVersionRef.current,
            }),
          })
          setSaving(false)

          const data = await response.json()
          if (!response.ok) throw { status: response.status, ...data }
          return data
        }),
      )
      .subscribe({
        // Error handling inside subscribe closes the stream on error (i.e after error autosaving stops)
        next: (data) => {
          if (data.lock_version) {
            lockVersionRef.current = data.lock_version
            setLastSavedAt(data.last_saved_at)
          }
        },
        error: (err) => {
          if (err.status == 409) {
            setConflict(true)
          }
        },
      })

    return () => sub.unsubscribe()
  }, [autoSave$, token])

  useCheckinSubscriber(
    (s) => s.data,
    (value) => autoSave$.next(value),
  )

  const onSubmit = () => {
    router.patch(
      `/forms/checkins/${token}/submit`,
      {
        lock_version: lockVersionRef.current,
        answers: formData,
      },
      { preserveScroll: true },
    )
  }

  return { onSubmit, conflict, lastSavedAt, saving }
}

export default useAutoSave
